import { z } from "zod";
import { PillowShapeSchema } from "./enums.js";
import { ProductTypeSchema } from "./products.js";
import { PROJECT_CONFIG_SIZES } from "./project-config.js";
import { CHECKOUT_MAX_QUANTITY_LIMIT } from "./settings.js";

// The Cart (ADR-0054 §2) is a **browser-local** list of `{ productId, quantity }`.
// These schemas are the wire contract between that local list and the two
// server-authoritative endpoints it feeds — the cart quote (re-price + re-verify)
// and the multi-line checkout — so the client NEVER sends a price, only ids +
// quantities. The Products the ids reference are server rows owned via the
// anonymous session; the cart itself is never persisted server-side.

// The two InPost Delivery methods (CONTEXT.md Delivery), as a standalone enum so a
// cart quote can be re-priced for a chosen method without embedding the full
// `OrderDelivery` (destination + receiver) the buyer only completes at checkout.
export const DELIVERY_METHODS = ["paczkomat", "courier"] as const;
export const DeliveryMethodSchema = z.enum(DELIVERY_METHODS);
export type DeliveryMethod = (typeof DELIVERY_METHODS)[number];

// One cart line as the client holds it: a server `productId` + a quantity. The
// quantity is bounded to the ABSOLUTE ceiling here (`CHECKOUT_MAX_QUANTITY_LIMIT`)
// as an edge sanity guard; the lower, operator-editable `checkout_max_quantity`
// (#240) is enforced server-side per line, exactly as the single-line checkout did.
export const CartItem = z.object({
  productId: z.guid(),
  quantity: z.number().int().min(1).max(CHECKOUT_MAX_QUANTITY_LIMIT),
});
export type CartItem = z.infer<typeof CartItem>;

// Request of `POST /cart/quote` (ADR-0054 §2/§3): the whole cart as a list of
// `{ productId, quantity }`, plus the method the buyer is previewing (default
// paczkomat). Every product is re-verified against the caller's anonymous session
// and re-priced from its CURRENT variant server-side — a client price is never
// accepted. `deliveryMethod` only selects which flat rate the returned `totalCents`
// reflects; both methods' rates come back so the checkout page recomputes live.
export const CartQuoteRequest = z.object({
  items: z.array(CartItem).min(1),
  deliveryMethod: DeliveryMethodSchema.default("paczkomat"),
});
export type CartQuoteRequest = z.infer<typeof CartQuoteRequest>;

// One priced line in a cart quote — a server-authoritative snapshot for display.
// The `unitPriceCents` is re-read from the product's current variant (never the
// client's); `lineTotalCents` is `unitPriceCents × quantity`. `projectId` is
// nullable for forward-compat with a future non-custom catalog item (ADR-0054 §1);
// every launch line carries one, which is what the cart tree groups on. `title` is the
// server-computed, per-type buyer-facing name (`variantTitlePl`) — the generic display
// axis the pillow-shaped fields promised to become once a second product type landed
// (#407). `shape` is now NULLABLE: it is the pillow's priced axis (drives the cart's
// no-preview size picker), absent on a 3D print, which prices on size + colour depth.
export const CartQuoteLine = z.object({
  productId: z.guid(),
  projectId: z.guid().nullable(),
  productType: ProductTypeSchema,
  variantId: z.guid(),
  // The buyer-facing product name, resolved server-side per type. The client renders it
  // verbatim (no client-side type switch), so a new type needs no cart-render change.
  title: z.string(),
  // Pillow-only priced axis; null for a 3D print. The cart's inline size picker
  // (#405, pillow-only) renders only when this is present.
  shape: PillowShapeSchema.nullable(),
  sizeCode: z.enum(PROJECT_CONFIG_SIZES),
  sizeCm: z.number().int().positive(),
  widthCm: z.number().int().positive(),
  heightCm: z.number().int().positive(),
  quantity: z.number().int().positive(),
  unitPriceCents: z.number().int().nonnegative(),
  lineTotalCents: z.number().int().nonnegative(),
  // The project's rendered thumbnail (freshly signed), or null when it has none
  // yet — the cart falls back to a quiet placeholder, like the checkout summary.
  thumbnailUrl: z.string().url().nullable(),
});
export type CartQuoteLine = z.infer<typeof CartQuoteLine>;

// Response of `POST /cart/quote`: the priced lines + the money breakdown, all
// server-authoritative. `subtotalCents` is the sum of the line totals; `shipping`
// carries BOTH flat method rates (#255) so the checkout page shows each and
// recomputes without a round-trip; `shippingCents` is the rate for the requested
// `deliveryMethod` and `totalCents = subtotalCents + shippingCents`. Shipping is a
// single flat per-method rate for the whole order (ADR-0054 §4); the oversize
// escalation rule slots in here (#406) behind the same seam.
//
// The quote is PARTIAL-TOLERANT: a cart line whose Product no longer resolves (GC-reaped
// after the 7-day anonymous window, ADR-0054 §7) or is no longer owned by the caller's
// session (its 7-day cookie expired) is DROPPED from `lines` and reported in
// `unavailableProductIds` instead of failing the whole quote. The browser-local cart
// outlives both of those windows, so a returning buyer's cart routinely holds dead ids;
// the client prunes them and re-quotes rather than dead-ending on an unretryable error.
export const CartQuoteResponse = z.object({
  lines: z.array(CartQuoteLine),
  // The requested product ids that could NOT be quoted — the client removes them from its
  // local cart. Deliberately carries NO reason: "gone" and "not yours" are indistinguishable
  // here, so the response is never an existence oracle for another session's product ids.
  // Defaulted so a payload written before this field (a test fixture, a mock) still parses.
  unavailableProductIds: z.array(z.guid()).default([]),
  subtotalCents: z.number().int().nonnegative(),
  shipping: z.object({
    paczkomatCents: z.number().int().nonnegative(),
    // Kurier rate INCLUDING the oversize surcharge when the order is oversize (#406) — so
    // the page shows, and the server charges, the true courier price with no client math.
    courierCents: z.number().int().nonnegative(),
    currency: z.string(),
    // The summed per-Variant shipping-size class across the cart (#406) — the parcel size
    // units `shippingForCart` compares against the Paczkomat capacity threshold.
    sizeUnits: z.number().int().nonnegative(),
    // True when the summed size units exceed the Paczkomat capacity: the order can't fit a
    // locker, so Paczkomat is withdrawn and the buyer is forced to Kurier InPost (#406).
    oversize: z.boolean(),
    // Whether Paczkomat may still be chosen (⇔ NOT oversize) — the checkout page greys out
    // and un-selects Paczkomat when false, and the server rejects an oversize Paczkomat POST.
    paczkomatAvailable: z.boolean(),
  }),
  deliveryMethod: DeliveryMethodSchema,
  shippingCents: z.number().int().nonnegative(),
  totalCents: z.number().int().nonnegative(),
  currency: z.string(),
  // Mirrors the `payments_disabled` kill-switch (ADR-0031) so the cart can disable
  // "Przejdź do kasy" / Pay up-front, exactly as the single-line quote does.
  paymentsEnabled: z.boolean(),
  // The live operator-editable per-line quantity ceiling (`checkout_max_quantity`,
  // #240) so the cart's quantity steppers enforce the same bound the server does.
  maxQuantity: z.number().int().positive(),
});
export type CartQuoteResponse = z.infer<typeof CartQuoteResponse>;
